import { ApolloError, useApolloClient } from "@apollo/client";
import React, { useEffect, useMemo } from "react";
import {
  useBoundariesMap,
  useFormAddressPosition,
} from "src/components/Boundary/useBoundary";
import { useGradeAnswer } from "src/components/Form/hooks/useGradeAnswer";
import { useVisibilityContext } from "src/components/Providers/FormProvider";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { GET_FORM_ANSWERS_BY_ID } from "src/scenes/parent/forms/graphql/queries";
import { useEligibilityService } from "src/services/eligibility/useEligibilityService";
import { getFormikInitialValues } from "src/services/formTemplate/answer";
import {
  getEligibleSchools,
  getEligibleSchoolsByLocation,
} from "src/services/formTemplate/preRankingSection";
import { getCompleteApplicableQuestions } from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import {
  GET_SCHOOLS_FOR_FORM,
  GET_SCHOOLS_FOR_ORGANIZATION,
} from "./graphql/queries";

type School = {
  readonly id: uuid;
  readonly name: string;
  readonly street_address: string | null;
  readonly isEligible?: boolean;
};

type Props = {
  readonly formId: uuid;
  readonly formTemplateId: uuid;
  readonly preRankingSection: AF.PreRankingSection<AF.WithId>;
  readonly selectedSchools: readonly School[];
  readonly enrollmentPeriodId: uuid;
  // Staff override: when true, return all active EP schools with an `isEligible`
  // flag instead of filtering ineligible ones out. Only set by the staff editor.
  readonly allowIneligible?: boolean;
};

function useFrontendEligibleSchools({
  formId,
  preRankingSection,
  selectedSchools,
  disabled,
  allowIneligible,
}: FrontendProps): {
  remoteData: RD.RemoteData<ApolloError, School[]>;
  refetch: () => Promise<void>;
} {
  const boundariesMapRemoteData = useBoundariesMap({ formId });
  const { remoteData: gradeConfigRemoteData, refetch: refetchGrade } =
    useGradeAnswer({ formId });

  const { remoteData: answersRemoteData, refetch: refetchAnswers } =
    useRemoteDataQuery<GQL.GetFormAnswersById, GQL.GetFormAnswersByIdVariables>(
      GET_FORM_ANSWERS_BY_ID,
      {
        variables: { form_id: formId },
        fetchPolicy: "no-cache",
        skip: disabled,
      }
    );
  const { remoteData: getSchoolsRemoteData, refetch: refetchSchools } =
    useRemoteDataQuery<
      GQL.GetSchoolsForFormTemplate,
      GQL.GetSchoolsForFormTemplateVariables
    >(GET_SCHOOLS_FOR_FORM, {
      variables: {
        form_id: formId,
      },
      fetchPolicy: "no-cache",
      skip: disabled,
    });

  const { remoteData: addressRD, refetch: refetchAddress } =
    useFormAddressPosition({
      formId,
    });
  const addressLocationRemoteData = addressRD
    .recover((e) => {
      console.error(e);
      return RD.success(undefined);
    })
    .mapError(
      (e) =>
        new ApolloError({
          errorMessage: e instanceof Error ? e.message : "Unknown",
        })
    );

  const visibilityContext = useVisibilityContext();

  const refetch = React.useCallback(async () => {
    await Promise.all([
      refetchSchools(),
      refetchAnswers(),
      refetchAddress(),
      refetchGrade(),
    ]);
  }, [refetchAddress, refetchAnswers, refetchGrade, refetchSchools]);

  const remoteData: RD.RemoteData<ApolloError, School[]> = React.useMemo(() => {
    if (disabled) {
      return RD.notAsked();
    }

    return RD.toTuple5(
      getSchoolsRemoteData,
      answersRemoteData,
      addressLocationRemoteData,
      boundariesMapRemoteData,
      gradeConfigRemoteData
    ).map(
      ([
        getSchoolsData,
        answersData,
        addressLocation,
        boundariesMap,
        gradeConfigId,
      ]) => {
        const { questions } = preRankingSection;
        // calculate eligible schools
        const answers = getFormikInitialValues(
          questions,
          answersData.form_answer,
          answersData.grades_answer,
          answersData.form_address,
          answersData.custom_question_answer
        );
        const completeQuestions = getCompleteApplicableQuestions(
          questions,
          answers,
          { rankedSchoolIds: [], previousFormSchoolIds: [] },
          visibilityContext
        );
        type GQLSchool = GQL.GetSchoolsForFormTemplate_school_by_form_school;
        const allSchools: School[] = getSchoolsData.school_by_form
          .map(({ school }) => school)
          .filter((school): school is GQLSchool => school !== null);

        let eligibleSchools: School[] = getEligibleSchools(
          allSchools,
          completeQuestions,
          answers
        ).filter(
          (school) =>
            // remove schools that are already selected
            !selectedSchools
              .map((school: School) => school.id)
              .includes(school.id)
        );

        eligibleSchools = eligibleSchools.filter(
          (school) =>
            // remove schools that are already selected
            !selectedSchools
              .map((school: School) => school.id)
              .includes(school.id)
        );

        const schools = getEligibleSchoolsByLocation(
          eligibleSchools,
          questions,
          gradeConfigId,
          boundariesMap,
          addressLocation
        );

        if (!allowIneligible) {
          return schools;
        }

        // Staff override: return all active schools with an eligibility flag,
        // excluding already-selected ones, instead of filtering ineligible out.
        const eligibleIds = new Set(schools.map((school) => school.id));
        const selectedIds = new Set(selectedSchools.map((school) => school.id));
        return allSchools
          .filter((school) => !selectedIds.has(school.id))
          .map((school) => ({
            ...school,
            isEligible: eligibleIds.has(school.id),
          }));
      }
    );
  }, [
    addressLocationRemoteData,
    answersRemoteData,
    boundariesMapRemoteData,
    disabled,
    getSchoolsRemoteData,
    gradeConfigRemoteData,
    preRankingSection,
    selectedSchools,
    visibilityContext,
    allowIneligible,
  ]);

  return { remoteData, refetch };
}

export function useEligibleSchools({
  formTemplateId,
  formId,
  enrollmentPeriodId,
  preRankingSection,
  selectedSchools,
  allowIneligible,
}: Props): {
  remoteData: RD.RemoteData<Error, School[]>;
  refetch: () => Promise<void>;
} {
  const organization = useOrganization();

  // Eligibility service is enabled
  const eligibilityService = useEligibilityService({ formTemplateId });
  const [ineligibleSchoolIdsRemoteData, setIneligibleSchoolIdsRemoteData] =
    React.useState<RD.RemoteData<Error, string[]>>(RD.loading());
  const apolloClient = useApolloClient();
  const { remoteData: schoolsRemoteData } = useRemoteDataQuery<
    GQL.GetSchoolsForOrganization,
    GQL.GetSchoolsForOrganizationVariables
  >(GET_SCHOOLS_FOR_ORGANIZATION, {
    variables: {
      organization_id: organization.toNullable()?.id ?? "",
      enrollment_period_id: enrollmentPeriodId,
    },
    skip: !eligibilityService.enabled || !organization.hasData(),
  });

  const fetchIneligibleSchoolIds = React.useCallback(async () => {
    try {
      setIneligibleSchoolIdsRemoteData(RD.loading());
      const ineligibleSchoolIds =
        await eligibilityService.getIneligibleSchoolIdsByFormId({
          formId,
        });
      setIneligibleSchoolIdsRemoteData(RD.success(ineligibleSchoolIds));
    } catch (err) {
      console.error(err);
      setIneligibleSchoolIdsRemoteData(
        RD.failure(
          err instanceof Error
            ? err
            : new Error("Unknown error", { cause: err })
        )
      );
    }
  }, [eligibilityService, formId]);

  useEffect(() => {
    if (!eligibilityService.enabled) {
      return;
    }

    fetchIneligibleSchoolIds();
  }, [
    apolloClient,
    eligibilityService,
    formId,
    preRankingSection.questions,
    fetchIneligibleSchoolIds,
  ]);

  const eligibleSchoolsRemoteData: RD.RemoteData<Error, School[]> =
    useMemo(() => {
      if (!eligibilityService.enabled) {
        return RD.notAsked();
      }

      return RD.map2(
        ineligibleSchoolIdsRemoteData,
        schoolsRemoteData,
        (ineligibleSchoolIds, schools) => {
          const selectedSchoolIds = selectedSchools.map((school) => school.id);
          const eligibleSchools = schools.school.flatMap((schoolByForm) => {
            if (schoolByForm === null) {
              return [];
            }
            if (selectedSchoolIds.includes(schoolByForm.id)) {
              // school is already selected
              return [];
            }
            const isEligible = !ineligibleSchoolIds.includes(schoolByForm.id);
            if (!isEligible && !allowIneligible) {
              // school is ineligible and staff override is off
              return [];
            }

            return [{ ...schoolByForm, isEligible }];
          });

          return eligibleSchools;
        }
      );
    }, [
      eligibilityService.enabled,
      ineligibleSchoolIdsRemoteData,
      schoolsRemoteData,
      selectedSchools,
      allowIneligible,
    ]);

  // Eligibility service is disabled
  const frontendEligibleSchools = useFrontendEligibleSchools({
    formId,
    preRankingSection,
    selectedSchools,
    disabled: eligibilityService.enabled,
    allowIneligible,
  });

  if (!eligibilityService.enabled) {
    return frontendEligibleSchools;
  }

  return {
    remoteData: eligibleSchoolsRemoteData,
    refetch: async () => {
      await fetchIneligibleSchoolIds();
    },
  };
}

type FrontendProps = {
  readonly formId: uuid;
  readonly preRankingSection: AF.PreRankingSection<AF.WithId>;
  readonly selectedSchools: readonly School[];
  readonly disabled: boolean;
  readonly allowIneligible?: boolean;
};
